import { useState, useEffect, useRef, useCallback } from 'react';
import { goalAvailablePointsFallback } from '../../utils/goalPoints';
import { isRewardComingSoon } from '../../utils/rewardComingSoon';

/**
 * Custom hook for Dealer reward/cart/voucher business logic.
 * Extracts cart management, Edenred, Tremendous, and voucher request logic from Dealer.js.
 */
const useDealerRewards = ({ user, apiCall, selectedCampaignDetail, campaignGoals, loadCampaignGoals, t }) => {
  // Cart state
  const [cart, setCart] = useState({});
  const [cartDenominations, setCartDenominations] = useState({});
  const [edenredCart, setEdenredCart] = useState({});
  const [edenredCustomValues, setEdenredCustomValues] = useState({});
  const [tremendousCustomValues, setTremendousCustomValues] = useState({});
  const [requestingTremendousVoucher, setRequestingTremendousVoucher] = useState(false);
  const [requestingVouchers, setRequestingVouchers] = useState(false);
  const [userRewardProviderConfig, setUserRewardProviderConfig] = useState(null);
  const [loadingUserConfig, setLoadingUserConfig] = useState(false);
  const [selectedSerialNumber, setSelectedSerialNumber] = useState('');
  const [requestingCard, setRequestingCard] = useState(false);

  // Voucher items state
  const [voucherItems, setVoucherItems] = useState([]);
  const [loadingVoucherItems, setLoadingVoucherItems] = useState(false);
  const [voucherItemsError, setVoucherItemsError] = useState('');

  // Pending voucher requests state
  const [pendingVoucherRequests, setPendingVoucherRequests] = useState([]);
  const [loadingPendingRequests, setLoadingPendingRequests] = useState(false);
  const previousPendingCountRef = useRef(0);

  // Voucher success dialog state
  const [showVoucherSuccessDialog, setShowVoucherSuccessDialog] = useState(false);

  // Tremendous reward success dialog state
  const [showTremendousRewardSuccessDialog, setShowTremendousRewardSuccessDialog] = useState(false);
  const [tremendousRewardLink, setTremendousRewardLink] = useState(null);
  const [tremendousRewardInfo, setTremendousRewardInfo] = useState(null);

  // Confirmation dialog state
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [confirmationPoints, setConfirmationPoints] = useState(0);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [confirmationCallback, setConfirmationCallback] = useState(null);
  const [confirmationRewardId, setConfirmationRewardId] = useState(null);

  const [feedbackDialog, setFeedbackDialog] = useState({
    open: false,
    variant: 'success',
    title: '',
    message: '',
  });

  const showFeedback = useCallback((variant, title, message = '') => {
    setFeedbackDialog({
      open: true,
      variant,
      title,
      message: message || '',
    });
  }, []);

  const closeFeedbackDialog = useCallback(() => {
    setFeedbackDialog((d) => ({ ...d, open: false }));
  }, []);

  // ── Helper functions ──

  const getAvailablePoints = () => {
    if (!campaignGoals || campaignGoals.length === 0) return 0;
    return goalAvailablePointsFallback(campaignGoals[0]);
  };

  const isEdenredReward = (rewardId) => {
    if (!selectedCampaignDetail?.rewards) return false;
    const reward = selectedCampaignDetail.rewards.find(r => r.id === parseInt(rewardId));
    return reward?.reward_provider?.name?.toLowerCase().includes('edenred') || false;
  };

  const isTremendousReward = (rewardId) => {
    if (!selectedCampaignDetail?.rewards) return false;
    const reward = selectedCampaignDetail.rewards.find(r => r.id === parseInt(rewardId));
    return reward?.reward_provider?.name?.toLowerCase().includes('tremendous') || false;
  };

  // ── Cart functions ──

  const getCartTotal = () => {
    if (!selectedCampaignDetail?.rewards) return 0;
    return Object.entries(cart).reduce((total, [rewardId, quantity]) => {
      if (isEdenredReward(rewardId)) return total;
      const reward = selectedCampaignDetail.rewards.find(r => r.id === parseInt(rewardId));
      if (reward && isRewardComingSoon(reward)) return total;
      const selectedDenom = cartDenominations[rewardId];
      if (selectedDenom) return total + selectedDenom.value * quantity;
      const defaultDenom = reward?.denominations?.find(d => d.is_default) || reward?.denominations?.[0];
      return total + (defaultDenom?.value || reward?.denomination || 0) * quantity;
    }, 0);
  };

  const getEdenredCartTotal = () => {
    if (!selectedCampaignDetail?.rewards) return 0;
    return Object.entries(edenredCart).reduce((total, [rewardId, quantity]) => {
      const customValue = edenredCustomValues[rewardId];
      const reward = selectedCampaignDetail.rewards.find(r => r.id === parseInt(rewardId));
      const denoms = reward?.denominations && reward.denominations.length > 0
        ? reward.denominations
        : (reward?.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
      const defaultDenom = denoms.find(d => d.is_default) || denoms[0];
      const valueToUse = customValue !== undefined ? customValue : (defaultDenom?.value || reward?.denomination || 0);
      return total + valueToUse * quantity;
    }, 0);
  };

  const addToCart = (rewardId, rewardCost) => {
    const reward = selectedCampaignDetail?.rewards?.find(r => r.id === parseInt(rewardId));
    if (reward && isRewardComingSoon(reward)) return;
    if (isEdenredReward(rewardId)) {
      addToEdenredCart(rewardId, rewardCost);
      return;
    }
    const currentQuantity = cart[rewardId] || 0;
    const newQuantity = currentQuantity + 1;
    const newCartTotal = getCartTotal() - (currentQuantity * rewardCost) + (newQuantity * rewardCost);
    if (newCartTotal <= getAvailablePoints()) {
      setCart(prev => ({ ...prev, [rewardId]: newQuantity }));
    }
  };

  const removeFromCart = (rewardId) => {
    if (isEdenredReward(rewardId)) {
      removeFromEdenredCart(rewardId);
      return;
    }
    setCart(prev => {
      const newCart = { ...prev };
      if (newCart[rewardId] > 1) { newCart[rewardId] -= 1; }
      else { delete newCart[rewardId]; }
      return newCart;
    });
  };

  const addToEdenredCart = (rewardId, rewardCost) => {
    const currentQuantity = edenredCart[rewardId] || 0;
    const newQuantity = currentQuantity + 1;
    const newCartTotal = getEdenredCartTotal() - (currentQuantity * rewardCost) + (newQuantity * rewardCost);
    if (newCartTotal <= getAvailablePoints()) {
      setEdenredCart(prev => ({ ...prev, [rewardId]: newQuantity }));
    }
  };

  const removeFromEdenredCart = (rewardId) => {
    setEdenredCart(prev => {
      const newCart = { ...prev };
      if (newCart[rewardId] > 1) { newCart[rewardId] -= 1; }
      else { delete newCart[rewardId]; }
      return newCart;
    });
  };

  const clearCart = () => { setCart({}); };
  const clearEdenredCart = () => { setEdenredCart({}); };

  const getTotalItemsInCart = () => {
    if (!selectedCampaignDetail?.rewards) return 0;
    return Object.entries(cart).reduce((total, [rewardId, quantity]) => {
      if (isEdenredReward(rewardId)) return total;
      const reward = selectedCampaignDetail.rewards.find(r => r.id === parseInt(rewardId));
      if (reward && isRewardComingSoon(reward)) return total;
      return total + quantity;
    }, 0);
  };

  const getTotalItemsInEdenredCart = () => {
    return Object.values(edenredCart).reduce((total, quantity) => total + quantity, 0);
  };

  // ── Edenred / Tremendous calculations ──

  const calculateEdenredTransferPoints = () => {
    if (!selectedCampaignDetail?.rewards) return 0;
    let total = 0;
    selectedCampaignDetail.rewards.forEach(reward => {
      if (!reward.reward_provider?.name?.toLowerCase().includes('edenred')) return;
      if (isRewardComingSoon(reward)) return;
      const denoms = reward.denominations && reward.denominations.length > 0
        ? reward.denominations
        : (reward.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
      const defaultDenom = denoms.find(d => d.is_default) || denoms[0];
      const defaultValue = defaultDenom?.value || reward.denomination || 0;
      const customValue = edenredCustomValues[reward.id] !== undefined ? edenredCustomValues[reward.id] : defaultValue;
      if (customValue > 0) { total += customValue; }
    });
    return total;
  };

  const calculateTremendousPoints = (rewardId) => {
    if (!selectedCampaignDetail?.rewards) return 0;
    const reward = selectedCampaignDetail.rewards.find(r => r.id === rewardId);
    if (!reward) return 0;
    const denoms = reward.denominations && reward.denominations.length > 0
      ? reward.denominations
      : (reward.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
    const defaultDenom = denoms.find(d => d.is_default) || denoms[0];
    const defaultValue = defaultDenom?.value || reward.denomination || 50;
    if (reward.any_value) {
      return tremendousCustomValues[rewardId] !== undefined ? tremendousCustomValues[rewardId] : defaultValue;
    }
    const denomsWithCode = denoms.filter(d => d.code && String(d.code).trim());
    const hasFixedDenominations = denomsWithCode.length > 0;
    if (hasFixedDenominations && denomsWithCode.length === 1) return denomsWithCode[0].value;
    if (hasFixedDenominations && denomsWithCode.length > 1) {
      const selected = tremendousCustomValues[rewardId];
      return selected !== undefined ? selected : (denomsWithCode[0]?.value ?? defaultValue);
    }
    return tremendousCustomValues[rewardId] !== undefined ? tremendousCustomValues[rewardId] : defaultValue;
  };

  const calculateCartSummaryPoints = () => {
    return getCartTotal() + getEdenredCartTotal();
  };

  // ── Confirmation dialog ──

  const showConfirmation = (points, action, callback, rewardId = null) => {
    setConfirmationPoints(points);
    setConfirmationAction(action);
    setConfirmationCallback(() => callback);
    setConfirmationRewardId(rewardId);
    setShowConfirmationDialog(true);
  };

  const handleConfirmedAction = async () => {
    setShowConfirmationDialog(false);
    if (confirmationCallback) { await confirmationCallback(); }
    setConfirmationPoints(0);
    setConfirmationAction(null);
    setConfirmationCallback(null);
    setConfirmationRewardId(null);
  };

  // ── Voucher request handlers ──

  const handleRequestVouchersClick = () => {
    const totalItems = getTotalItemsInCart();
    if (totalItems === 0) {
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.cartEmpty'));
      return;
    }
    const points = calculateCartSummaryPoints();
    if (points <= 0) {
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.addItemsToCart'));
      return;
    }
    showConfirmation(points, 'cart', handleRequestVouchers);
  };

  const handleTransferMoneyClick = () => {
    if (!selectedSerialNumber) {
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.selectSerialNumber'));
      return;
    }
    const points = calculateEdenredTransferPoints();
    if (points <= 0) {
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.enterValidAmount'));
      return;
    }
    showConfirmation(points, 'edenred', handleRequestVouchers);
  };

  const handleRequestVouchers = async () => {
    const totalGiftMallItems = getTotalItemsInCart();
    const hasEdenredValue = selectedCampaignDetail?.rewards?.some(reward => {
      if (!reward.reward_provider?.name?.toLowerCase().includes('edenred')) return false;
      if (isRewardComingSoon(reward)) return false;
      const denoms = reward.denominations && reward.denominations.length > 0
        ? reward.denominations
        : (reward.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
      const defaultDenom = denoms.find(d => d.is_default) || denoms[0];
      const defaultValue = defaultDenom?.value || reward.denomination || 0;
      const customValue = edenredCustomValues[reward.id] !== undefined ? edenredCustomValues[reward.id] : defaultValue;
      return customValue > 0;
    });
    if (totalGiftMallItems === 0 && !hasEdenredValue) return;
    if (!selectedCampaignDetail) {
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.noCampaignSelected'));
      return;
    }

    setRequestingVouchers(true);
    setVoucherItemsError('');

    try {
      const vouchers = [];
      Object.entries(cart).forEach(([rewardId, quantity]) => {
        if (quantity > 0 && !isEdenredReward(rewardId)) {
          const reward = selectedCampaignDetail.rewards.find(r => r.id === parseInt(rewardId));
          if (reward && isRewardComingSoon(reward)) return;
          const denom = cartDenominations[rewardId];
          vouchers.push({ reward_id: parseInt(rewardId), quantity, denomination: denom?.value || 0 });
        }
      });

      const hasEdenredRewards = selectedCampaignDetail.rewards?.some(reward =>
        reward.reward_provider?.name?.toLowerCase().includes('edenred') &&
        !isRewardComingSoon(reward) &&
        (edenredCustomValues[reward.id] !== undefined ? edenredCustomValues[reward.id] :
          (reward.denominations?.find(d => d.is_default)?.value || reward.denomination || 0)) > 0
      );

      if (hasEdenredRewards && selectedSerialNumber) {
        let edenredTotalAmount = 0;
        const edenredVoucherLines = [];
        selectedCampaignDetail.rewards.forEach(reward => {
          if (!reward.reward_provider?.name?.toLowerCase().includes('edenred')) return;
          if (isRewardComingSoon(reward)) return;
          const denoms = reward.denominations && reward.denominations.length > 0
            ? reward.denominations
            : (reward.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
          const defaultDenom = denoms.find(d => d.is_default) || denoms[0];
          const defaultValue = defaultDenom?.value || reward.denomination || 0;
          const customValue = edenredCustomValues[reward.id] !== undefined ? edenredCustomValues[reward.id] : defaultValue;
          if (customValue > 0) {
            edenredTotalAmount += customValue;
            edenredVoucherLines.push({
              reward_id: reward.id,
              quantity: 1,
              denomination: Number(customValue),
            });
          }
        });

        let companyApiAccount = selectedCampaignDetail.company?.api_account;
        if (!companyApiAccount && user?.company_id) {
          try {
            const companyResult = await apiCall('get', `/api/companies/${user.company_id}`);
            if (companyResult.success) { companyApiAccount = companyResult.data.api_account; }
          } catch (error) { console.error('Error fetching company:', error); }
        }
        if (!companyApiAccount) {
          showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.companyApiNotConfigured'));
          setRequestingVouchers(false);
          return;
        }

        const edenredResult = await apiCall('post', '/api/dealer/edenred-transfer', {
          serial_number: selectedSerialNumber,
          amount: Math.round(edenredTotalAmount),
          account: companyApiAccount,
          campaign_id: selectedCampaignDetail.id,
          vouchers: edenredVoucherLines,
        });
        if (edenredResult.success) {
          showFeedback(
            'success',
            t('errors.moneyTransferredSuccess'),
            t('dialogs.edenredTransferSuccessDetails'),
          );
          setEdenredCustomValues({});
          setSelectedSerialNumber('');
          await loadCampaignGoals(selectedCampaignDetail.id);
          await loadVoucherItems(selectedCampaignDetail.id);
          await loadPendingVoucherRequests(selectedCampaignDetail.id);
        } else if (!edenredResult.requiresAuth) {
          showFeedback(
            'error',
            t('dialogs.transferFailedTitle'),
            (edenredResult.error || 'Unknown error').trim(),
          );
        }
      } else {
        if (selectedCampaignDetail.rewards) {
          selectedCampaignDetail.rewards.forEach(reward => {
            if (!reward.reward_provider?.name?.toLowerCase().includes('edenred')) return;
            if (isRewardComingSoon(reward)) return;
            const denoms = reward.denominations && reward.denominations.length > 0
              ? reward.denominations
              : (reward.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
            const defaultDenom = denoms.find(d => d.is_default) || denoms[0];
            const defaultValue = defaultDenom?.value || reward.denomination || 0;
            const customValue = edenredCustomValues[reward.id] !== undefined ? edenredCustomValues[reward.id] : defaultValue;
            if (customValue > 0) {
              vouchers.push({ reward_id: reward.id, quantity: 1, denomination: customValue });
            }
          });
        }

        const requestData = { user_id: user.id, campaign_id: selectedCampaignDetail.id, vouchers };
        const result = await apiCall('post', '/api/vouchers/request', requestData);
        if (result.success) {
          setShowVoucherSuccessDialog(true);
          clearCart();
          setEdenredCustomValues({});
          await loadCampaignGoals(selectedCampaignDetail.id);
          await loadVoucherItems(selectedCampaignDetail.id);
          await loadPendingVoucherRequests(selectedCampaignDetail.id);
        } else if (!result.requiresAuth) {
          showFeedback(
            'error',
            t('dialogs.voucherOrderFailedTitle'),
            (result.error || 'Unknown error').trim(),
          );
        }
      }
    } catch (error) {
      console.error('Error requesting vouchers:', error);
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.failedRequestVouchersRetry'));
    }
    setRequestingVouchers(false);
  };

  // ── Tremendous voucher ──

  const handleRequestTremendousVoucherClick = (rewardId) => {
    if (!selectedCampaignDetail || !user?.id || !user?.company_id) {
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.missingRequiredInfo'));
      return;
    }
    const reward = selectedCampaignDetail.rewards.find(r => r.id === rewardId);
    if (!reward) {
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.rewardNotFound'));
      return;
    }
    if (isRewardComingSoon(reward)) return;
    const points = calculateTremendousPoints(rewardId);
    if (points <= 0) {
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.enterValidAmount'));
      return;
    }
    if (points > 2000) {
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.maxValueTremendous'));
      return;
    }
    const availablePoints = getAvailablePoints();
    if (points > availablePoints) {
      showFeedback(
        'error',
        t('dialogs.errorNoticeTitle'),
        t('errors.insufficientPoints', { available: availablePoints, required: points }),
      );
      return;
    }
    showConfirmation(points, 'tremendous', () => handleRequestTremendousVoucher(rewardId), rewardId);
  };

  const handleRequestTremendousVoucher = async (rewardId) => {
    if (!selectedCampaignDetail || !user?.id || !user?.company_id) {
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.missingRequiredInfo'));
      return;
    }
    const reward = selectedCampaignDetail.rewards.find(r => r.id === rewardId);
    if (!reward) {
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.rewardNotFound'));
      return;
    }
    if (isRewardComingSoon(reward)) return;

    const denoms = reward.denominations && reward.denominations.length > 0
      ? reward.denominations
      : (reward.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
    const defaultDenom = denoms.find(d => d.is_default) || denoms[0];
    const defaultValue = defaultDenom?.value || reward.denomination || 50;
    const denomsWithCode = denoms.filter(d => d.code && String(d.code).trim());
    const hasFixedDenominations = !reward.any_value && denomsWithCode.length > 0;
    const customValue = reward.any_value
      ? (tremendousCustomValues[rewardId] !== undefined ? tremendousCustomValues[rewardId] : defaultValue)
      : (hasFixedDenominations && denomsWithCode.length === 1
        ? denomsWithCode[0].value
        : (tremendousCustomValues[rewardId] !== undefined ? tremendousCustomValues[rewardId] : (denomsWithCode.length > 1 ? (denomsWithCode[0]?.value ?? defaultValue) : defaultValue)));

    if (customValue <= 0) {
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.enterValidAmount'));
      return;
    }
    if (customValue > 2000) {
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.maxValueTremendous'));
      return;
    }
    const availablePoints = getAvailablePoints();
    if (customValue > availablePoints) {
      showFeedback(
        'error',
        t('dialogs.errorNoticeTitle'),
        t('errors.insufficientPoints', { available: availablePoints, required: customValue }),
      );
      return;
    }

    setRequestingTremendousVoucher(true);
    try {
      const amountCents = Math.round(customValue * 100);
      const recipientEmail = user.email;
      const recipientName = [user.name, user.last_name].filter(Boolean).join(' ') || user.email.split('@')[0];
      const result = await apiCall('post', '/api/tremendous/rewards/create', {
        company_id: user.company_id,
        campaign_id: selectedCampaignDetail.id,
        user_id: user.id,
        reward_id: reward.id,
        amount_cents: amountCents,
        recipient_email: recipientEmail,
        recipient_name: recipientName
      });

      if (result.success && result.data?.reward_link) {
        const rewardDenoms = reward.denominations && reward.denominations.length > 0
          ? reward.denominations
          : (reward.denomination ? [{ value: reward.denomination, currency: reward.currency || 'USD', is_default: true }] : []);
        const rewardDefaultDenom = rewardDenoms.find(d => d.is_default) || rewardDenoms[0];
        const currency = rewardDefaultDenom?.currency || reward.currency || 'USD';
        setTremendousRewardLink(result.data.reward_link);
        setTremendousRewardInfo({
          rewardName: reward.name, amount: customValue, currency,
          orderId: result.data.order?.id || ''
        });
        setTremendousCustomValues(prev => {
          const newValues = { ...prev }; delete newValues[rewardId]; return newValues;
        });
        await loadCampaignGoals(selectedCampaignDetail.id);
        await loadVoucherItems(selectedCampaignDetail.id);
        setShowTremendousRewardSuccessDialog(true);
      } else if (!result.requiresAuth) {
        showFeedback(
          'error',
          t('dialogs.voucherOrderFailedTitle'),
          (result.error || 'Unknown error').trim(),
        );
      }
    } catch (error) {
      console.error('Error requesting Tremendous voucher:', error);
      showFeedback('error', t('dialogs.errorNoticeTitle'), t('errors.failedRequestVoucherRetry'));
    }
    setRequestingTremendousVoucher(false);
  };

  // ── Voucher items loading ──

  const loadVoucherItems = async (campaignId, skipLoading = false) => {
    if (!user?.id) return;
    if (!skipLoading) setLoadingVoucherItems(true);
    setVoucherItemsError('');
    try {
      const timestamp = new Date().getTime();
      const url = `/api/vouchers/items?campaign_id=${campaignId}&user_id=${user.id}&_t=${timestamp}`;
      const result = await apiCall('get', url);
      if (result.success) {
        setVoucherItems(result.data.voucher_items || []);
      } else if (!result.requiresAuth) {
        setVoucherItemsError(result.error || t('errors.failedLoadVoucherItems'));
      }
    } catch (error) {
      console.error('Error loading voucher items:', error);
      setVoucherItemsError(t('errors.failedLoadVoucherItems'));
    }
    if (!skipLoading) setLoadingVoucherItems(false);
  };

  const loadPendingVoucherRequests = async (campaignId) => {
    if (!user?.id) return;
    setLoadingPendingRequests(true);
    try {
      const result = await apiCall('get', `/api/vouchers/requests?campaign_id=${campaignId}&user_id=${user.id}&status=ORDERED`);
      if (result.success) {
        const requests = result.data.voucher_requests || [];
        const previousCount = previousPendingCountRef.current;
        const currentCount = requests.length;
        const processingIsDone = previousCount > 0 && currentCount === 0;
        previousPendingCountRef.current = currentCount;
        setPendingVoucherRequests(requests);
        if (processingIsDone) {
          setTimeout(async () => { await loadVoucherItems(campaignId, true); }, 1000);
        }
      }
    } catch (error) {
      console.error('Error loading pending voucher requests:', error);
    }
    setLoadingPendingRequests(false);
  };

  // ── Edenred user config loading ──

  const loadUserRewardProviderConfig = async (rewardProviderId) => {
    if (!rewardProviderId || !apiCall) return;
    setLoadingUserConfig(true);
    try {
      const result = await apiCall('get', `/api/my-reward-providers/${rewardProviderId}`);
      if (result.success) {
        setUserRewardProviderConfig(result.data);
      } else {
        setUserRewardProviderConfig(null);
      }
    } catch (error) {
      console.error('Error loading user reward provider config:', error);
      setUserRewardProviderConfig(null);
    }
    setLoadingUserConfig(false);
  };

  // Default Edenred card dropdown to the first active card when config loads or selection is cleared (e.g. after transfer).
  useEffect(() => {
    const serials = userRewardProviderConfig?.config?.serialNumbers;
    if (!Array.isArray(serials) || serials.length === 0) return;
    setSelectedSerialNumber((prev) => {
      if (prev && serials.includes(prev)) return prev;
      return serials[0];
    });
  }, [userRewardProviderConfig, selectedSerialNumber]);

  // ── Polling effect for pending voucher requests ──

  useEffect(() => {
    if (!selectedCampaignDetail?.id || !apiCall) return;
    if (pendingVoucherRequests.length === 0) return;
    const pollInterval = setInterval(() => {
      loadPendingVoucherRequests(selectedCampaignDetail.id);
    }, 10000);
    return () => clearInterval(pollInterval);
  }, [selectedCampaignDetail?.id, apiCall, pendingVoucherRequests.length]);

  // Load Edenred user config when campaign has Edenred rewards (row in user_reward_providers)
  useEffect(() => {
    if (!selectedCampaignDetail?.rewards || !apiCall) return;
    const edenredReward = selectedCampaignDetail.rewards.find(r =>
      r.reward_provider?.name?.toLowerCase().includes('edenred')
    );
    if (edenredReward?.reward_provider?.id) {
      loadUserRewardProviderConfig(edenredReward.reward_provider.id);
    } else {
      setUserRewardProviderConfig(null);
      setSelectedSerialNumber('');
    }
  }, [selectedCampaignDetail, apiCall]);

  return {
    // Cart
    cart, cartDenominations, setCartDenominations,
    addToCart, removeFromCart, getCartTotal, clearCart,
    getTotalItemsInCart,
    // Edenred cart
    edenredCart, edenredCustomValues, setEdenredCustomValues,
    clearEdenredCart, getEdenredCartTotal, getTotalItemsInEdenredCart,
    calculateEdenredTransferPoints,
    // Tremendous
    tremendousCustomValues, setTremendousCustomValues,
    requestingTremendousVoucher, calculateTremendousPoints,
    handleRequestTremendousVoucherClick,
    // Voucher requests
    requestingVouchers, handleRequestVouchersClick, handleTransferMoneyClick,
    // Voucher items
    voucherItems, setVoucherItems, loadingVoucherItems, voucherItemsError, setVoucherItemsError,
    loadVoucherItems, loadPendingVoucherRequests,
    // Pending requests
    pendingVoucherRequests, setPendingVoucherRequests, loadingPendingRequests,
    // Success dialogs
    showVoucherSuccessDialog, setShowVoucherSuccessDialog,
    showTremendousRewardSuccessDialog, setShowTremendousRewardSuccessDialog,
    tremendousRewardLink, setTremendousRewardLink,
    tremendousRewardInfo, setTremendousRewardInfo,
    // Confirmation dialog
    showConfirmationDialog, setShowConfirmationDialog,
    confirmationPoints, confirmationAction, confirmationRewardId,
    handleConfirmedAction,
    // Feedback (success / error) dialog
    feedbackDialog, showFeedback, closeFeedbackDialog,
    // User reward config (Edenred)
    userRewardProviderConfig, setUserRewardProviderConfig, loadingUserConfig,
    selectedSerialNumber, setSelectedSerialNumber,
    requestingCard, setRequestingCard,
    // Helpers
    getAvailablePoints, isEdenredReward, isTremendousReward,
    calculateCartSummaryPoints,
  };
};

export default useDealerRewards;
